import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  // Handle null, undefined, or NaN values
  if (amount == null || isNaN(amount)) {
    return '$0.00';
  }

  const numAmount = Number(amount);
  if (numAmount >= 1000000) {
    return `$${(numAmount / 1000000).toFixed(1)}M`;
  }
  if (numAmount >= 1000) {
    return `$${(numAmount / 1000).toFixed(1)}K`;
  }
  return `$${numAmount.toFixed(2)}`;
}

export function formatPercent(value: number): string {
  if (value == null || isNaN(value)) {
    return '0.0%';
  }
  return `${Number(value).toFixed(1)}%`;
}

export function formatNumber(value: number, decimals: number = 2): string {
  if (value == null || isNaN(value)) {
    return '0';
  }
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Copy text to clipboard with fallback for older browsers
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch {
    return false;
  }
}

/**
 * Format a date as a relative time string (e.g., "2 minutes ago", "in 3 hours")
 */
export function formatRelativeTime(date: Date | string | number): string {
  const now = new Date();
  const target = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - target.getTime()) / 1000);
  const absDiff = Math.abs(diffInSeconds);
  const isFuture = diffInSeconds < 0;

  const intervals: { label: string; seconds: number }[] = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'week', seconds: 604800 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
    { label: 'second', seconds: 1 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(absDiff / interval.seconds);
    if (count >= 1) {
      const plural = count !== 1 ? 's' : '';
      if (isFuture) {
        return `in ${count} ${interval.label}${plural}`;
      }
      return `${count} ${interval.label}${plural} ago`;
    }
  }

  return 'just now';
}

/**
 * Format a date with both relative and absolute time
 */
export function formatTimeWithTooltip(date: Date | string | number): {
  relative: string;
  absolute: string;
} {
  const target = new Date(date);
  return {
    relative: formatRelativeTime(date),
    absolute: target.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }),
  };
}

/**
 * Truncate a string with ellipsis
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength - 3)}...`;
}

/**
 * Truncate in the middle (useful for IDs, hashes, addresses)
 */
export function truncateMiddle(str: string, startChars: number = 6, endChars: number = 4): string {
  if (str.length <= startChars + endChars + 3) return str;
  return `${str.slice(0, startChars)}...${str.slice(-endChars)}`;
}
